import UserService from "./UserService";
import Kichiri from "./KichiriService";
import { STATUSES } from "../constants/Tasks";

const TaskService = {
	/**
	 * Sends a task back to the previous step with a reason
	 * @param {Number} taskId
	 * @param {String} reason
	 * @returns {Object} The task that was re-woken, or null on failure
	 */
	async sendBack({ taskId, reason }) {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.task.sendBack({ taskId, reason }, {}, authToken);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Fetch a task
	 * @param {Number} taskId
	 * @returns {Object}
	 */
	async fetchTask({ taskId }) {
		let { authToken } = UserService.getUserData();

		try {
			let { data } = await Kichiri.task.get(
				{
					taskId
				},
				{},
				authToken
			);

			return data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Update a task
	 * @param {Number} taskId
	 * @param {String} name
	 * @param {Number} engagementId
	 * @param {Number} assignedUserId
	 * @param {Number} clientId
	 * @param {Number} clientId
	 * @param {String} description
	 * @param {Date} deadline
	 * @param {String} notes
	 * @param {String} status
	 * @param {Boolean} notify
	 * @param {Number|null} assignedBy
	 */
	async update({ taskId, name, engagementId, assignedUsers, clientId, description, deadline, notes, status, notify, isDeleted, assignedBy }) {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.task.update(
				{
					taskId,
					name,
					engagementId,
					assignedUsers,
					clientId,
					description,
					deadline,
					notes,
					status,
					notify,
					isDeleted,
					assignedBy
				},
				{},
				authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Create a task
	 * @param {String} name
	 * @param {Number} engagementId
	 * @param {Number} assignedUserId
	 * @param {Number} clientId
	 * @param {String} description
	 * @param {Date} deadline
	 * @param {String} notes
	 * @param {String} status
	 * @param {Boolean} notify
	 * @param {Number|null} assignedBy
	 */
	async create({ name, engagementId, clientId, description, deadline, notes, status, notify, assignedUsers, assignedBy }) {
		let { authToken } = UserService.getUserData();

		try {
			await Kichiri.task.create(
				{
					name,
					engagementId,
					clientId,
					description,
					deadline,
					notes,
					status,
					notify,
					assignedUsers,
					assignedBy
				},
				{},
				authToken
			);
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Fetches a list of tasks
	 * @param {Number} assignedUserId
	 * @param {Number} engagementId
	 * @param {Number} clientId
	 * @param {String} filter
	 * @param {String} sortOrder
	 * @param {String} sortField
	 * @param {String} searchTerm
	 * @returns {Array}
	 */
	async list({
		searchTerm,
		sortOrder,
		sortField,
		limit = null,
		offset = 0,
		clientId,
		engagementId,
		assignedUsers,
		assignedStatuses,
		assignedClients,
		assignedClientGroups,
		assignedEngagements,
		dueDate,
		assignedByOther,
		assignedBy,
		shouldShowInactive,
		countOnly,
		facets
	}) {
		let { authToken } = UserService.getUserData();

		try {
			if (assignedUsers) {
				assignedUsers = JSON.stringify(assignedUsers);
			}

			if (assignedEngagements) {
				assignedEngagements = JSON.stringify(assignedEngagements);
			}

			let response = await Kichiri.task.list(
				{},
				{
					searchTerm,
					sortOrder,
					sortField,
					limit,
					offset,

					clientId,
					engagementId,
					assignedUsers,
					assignedStatuses,
					assignedClients,
					assignedClientGroups,
					assignedEngagements,
					dueDate,
					assignedByOther,
					assignedBy,
					shouldShowInactive,
					countOnly,
					facets
				},
				authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return [];
	},

	/**
	 * Counts per status, due preset, assignee, client, client group and engagement for the given scope
	 * @returns {Object|null}
	 */
	async facets(params) {
		let result = await this.list({ ...params, facets: true });

		if (result && typeof result === "object" && !Array.isArray(result)) {
			return result;
		}

		return null;
	},

	/**
	 * Counts the tasks matching the same filters as list(), without downloading them
	 * @returns {Number}
	 */
	async count(params) {
		let result = await this.list({ ...params, countOnly: true });

		if (result && typeof result.count === "number") {
			return result.count;
		}

		return 0;
	},

	async fetchTasksStatuses({ search }) {
		try {
			let statuses = Object.values(STATUSES);

			let filteredStatuses = statuses.filter(status => {
				let value = status.name.toLowerCase();

				return value.indexOf(search) !== -1;
			});

			return filteredStatuses;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Update tasks (one or more)
	 * @param {Array} taskIds - list of task id's to delete
	 * @return {String} response message
	 */
	async bulkUpdate({ taskIds, action }) {
		let { authToken } = UserService.getUserData();

		try {
			// call endpoint to update the tasks
			let response = await Kichiri.task.bulk_update({ taskIds, action }, {}, authToken);

			// return with response
			return response;
		} catch (error) {
			console.log(error);
		}
	}
};

export default TaskService;
